import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form, { FormInstance } from "antd/es/form";
import Select from "antd/es/select";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import stringHash from "@mapmycustomers/shared/util/hash/stringHash";
import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";
import { Button, FormItem, SelectField, TextAreaWithMentions } from "@mapmycustomers/ui";

import AssigneeSearch from "@app/component/activity/AssigneeSearch";
import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import ActivityCompleted from "@app/component/createEditEntity/Activity/component/ActivityCompleted";
import LockedEntityType from "@app/component/createEditEntity/Activity/component/LockedFormFields/LockedEntityType";
import FormValues from "@app/component/createEditEntity/Activity/types/FormValues";
import getActivityPayloadFromCreationForm from "@app/component/createEditEntity/Activity/utils/getActivityPayloadFromCreationForm";
import getFormInitialValues from "@app/component/createEditEntity/Activity/utils/getFormInitialValues";
import useMentionsValidation from "@app/component/createEditEntity/Activity/utils/useMentionsValidation";
import FormFields from "@app/component/FormFields";
import useFormSchema from "@app/component/FormFields/utils/useFormSchema";
import FormLayoutSelector from "@app/component/FormLayoutSelector";
import VisibilitySelectField from "@app/component/input/VisibilitySelectField";
import EntityModal from "@app/component/modal/EntityModal";
import { getActivityTypes } from "@app/store/activity";
import { ActivityPayload } from "@app/store/api/ApiService";
import { getCurrentUser, getMe, isCurrentUserManager, isCurrentUserOwner } from "@app/store/iam";
import { getTeams, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { isUniqueCustomField } from "@app/util/fieldModel/impl/assert";
import useFormChangeTracker from "@app/util/hook/form/useFormChangeTracker";
import useActivityVisibilityOptions from "@app/util/hook/useActivityVisibilityOptions";
import useCustomFieldValidation from "@app/util/hook/useCustomFieldValidation";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import { activityLayoutModel } from "@app/util/layout/impl";
import { getActivityVisibilityNote } from "@app/util/ui";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import NotificationSettings from "../../NotificationSettings";
import ActivityDate from "../component/ActivityDate";
import File from "../component/File";
import LockedActivityName from "../component/LockedFormFields/LockedActivityName";
import { bulkCreateActivities } from "../store/actions";

import styles from "./BulkActivityCreateModal.module.scss";
import messages from "./messages";

const noFilter = () => true;

type BulkActivityCreateModalProps = {
  activities: Partial<ActivityPayload>[];
  className?: string;
  count: number;
  defaultAddedFields?: LayoutSchemaField[];
  form: FormInstance<FormValues>;
  initialValue?: Partial<Omit<Activity, "id">>;
  isFieldDisabled?: (field: IField) => boolean;
  lockedFieldsProps?: Record<string, { caption?: string; placeholder?: string }>;
  onHide: (created?: boolean) => void;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const BulkActivityCreateModal: React.FC<BulkActivityCreateModalProps> = ({
  activities,
  activityTypes,
  className,
  count,
  currentUser,
  defaultAddedFields,
  form,
  initialValue,
  isFieldDisabled,
  isManager,
  isOwner,
  lockedFieldsProps,
  me,
  onBulkCreate,
  onHide,
  teams,
  users,
}) => {
  const intl = useIntl();
  const forceRender = useRerenderer();

  const [isFormChanged, handleFormChanges] = useFormChangeTracker(form);
  const [formLayout, setFormLayout] = useState(activityLayoutModel.defaultFormLayout!);

  const defaultLayoutActivityTypeId = useMemo(() => {
    return formLayout?.schema.find((field) => field.field === "crmActivityTypeId")
      ?.defaultValue?.[0] as ActivityType["id"];
  }, [formLayout]);

  const initialValues = getFormInitialValues(
    initialValue,
    currentUser,
    defaultLayoutActivityTypeId,
    false,
    ActivityVisibility.PRIVATE,
    true
  );

  const [formFieldsRef, handleFinishFail] = useErroredFieldsScroller();

  const visibilityOptions = useActivityVisibilityOptions(isOwner, isManager);

  const [actualSchema] = useFormSchema(EntityType.ACTIVITY, formLayout, noFilter, true);

  const isCompleted = form.getFieldValue(ActivityFieldName.COMPLETED);

  const uniqueCustomFields = useMemo(
    () => activityFieldModel.fields.filter(isUniqueCustomField),
    []
  );

  const hasRequiredUniqueCustomFields = useMemo(
    () =>
      actualSchema.some((schemaField) => {
        const field = activityFieldModel.getByPlatformName(schemaField.field);
        return schemaField.required && field && isUniqueCustomField(field);
      }),
    [actualSchema]
  );

  const hasRequiredRelationshipField = useMemo(() => {
    return actualSchema.some((schemaField) => {
      if (!schemaField.required) {
        return false;
      }

      const field = activityFieldModel.getByPlatformName(schemaField.field);
      if (!field || !field.hasFeature(FieldFeature.RELATIONSHIPS)) {
        return false;
      }

      return isFieldDisabled?.(field);
    });
  }, [actualSchema, isFieldDisabled]);

  const [, allowedUserEmails, handleAllMentionedUsersHaveAccessToActivity] = useMentionsValidation(
    me,
    users
  );

  const handleFormChangeRerender = useCallback(() => {
    handleFormChanges();
    forceRender();
  }, [forceRender, handleFormChanges]);

  const handleIsMentionDisabled = useCallback(
    (user: User) => !allowedUserEmails?.includes(user.username),
    [allowedUserEmails]
  );

  const handleValuesChange = useCallback(
    (changedValues, values) => {
      [handleAllMentionedUsersHaveAccessToActivity].forEach((fn) => fn(changedValues, values));
    },
    [handleAllMentionedUsersHaveAccessToActivity]
  );

  const handleCustomFieldValidate = useCustomFieldValidation(form, handleFinishFail, true);

  const handleFormFinish = useCallback(() => {
    if (!formLayout) {
      return;
    }

    const values = form.getFieldsValue();
    const { customFields, ...activity } = getActivityPayloadFromCreationForm(values, activityTypes);

    const customFieldsValues = values.customFields
      ? Object.values<CustomFieldValue>(values.customFields).filter(isDefined)
      : [];

    const activitiesToCreate = activities.map((currentActivity) => ({
      ...activity,
      ...currentActivity,
    }));

    onBulkCreate({
      activities: activitiesToCreate,
      callback: () => onHide(true),
      customFieldsValues,
      customFieldsValueValidateCallback: handleCustomFieldValidate,
      layoutId: formLayout.id,
    });
  }, [
    formLayout,
    form,
    activityTypes,
    activities,
    onBulkCreate,
    onHide,
    handleCustomFieldValidate,
  ]);

  const activityTypeOptions = useActivityTypeOptions(
    activityTypes,
    form.getFieldValue("activityTypeId"),
    useCallback((type) => !type.active, [])
  );

  useEffect(() => {
    uniqueCustomFields.map((field) => {
      form.setFieldValue(["customFields", field.customFieldData.esKey], undefined);
    });
  }, [form, uniqueCustomFields]);

  return (
    <EntityModal
      actions={<NotificationSettings form={form} onChange={handleFormChanges} />}
      className={cn(styles.modal, className)}
      footer={null}
      maskClosable={false}
      onCancel={() => onHide()}
      showConfirm={isFormChanged}
      title={
        <>
          <div className={styles.title}>{intl.formatMessage(messages.title, { count })}</div>
          {activityLayoutModel.layouts.length > 1 && (
            <FormLayoutSelector
              entityType={EntityType.ACTIVITY}
              layout={formLayout}
              onChange={setFormLayout}
            />
          )}
        </>
      }
      width="40rem"
    >
      <div className={styles.formWrapper} ref={formFieldsRef}>
        <Form<FormValues>
          className={styles.form}
          form={form}
          initialValues={initialValues}
          layout="vertical"
          onFinish={handleFormFinish}
          onFinishFailed={handleFinishFail}
          onValuesChange={handleValuesChange}
          preserve
        >
          <div className={styles.container}>
            <div className={styles.note}>{intl.formatMessage(messages.note)}</div>
            <Form.Item name="reminders" noStyle />

            {formLayout && (
              <FormFields
                defaultAddedFields={defaultAddedFields}
                disableUniqueCustomFields
                entityType={EntityType.ACTIVITY}
                fileComponent={File}
                layout={formLayout}
              >
                <ConditionalFormField feature={FieldFeature.ACTIVITY_TYPE_FIELD}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <>
                      <FormItem label={label} name="activityTypeId" required={required}>
                        <SelectField<ActivityType["id"]>
                          className={styles.select}
                          disabled={disabled}
                          dropdownMatchSelectWidth={false}
                          filterOption={(inputValue, option) =>
                            (
                              activityTypes.find(
                                (type) => type.id === parseInt(`${option?.value || 0}`)
                              )?.name as string
                            )
                              ?.toLowerCase()
                              .includes(inputValue.toLowerCase().trim()) ?? false
                          }
                          label={label}
                          placeholder={intl.formatMessage(messages.select)}
                          required={required}
                          showSearch
                        >
                          {activityTypeOptions.map((type) => (
                            <Select.Option
                              className={cn(styles.option, { [styles.disabled]: type.disabled })}
                              disabled={type.disabled}
                              key={type.value}
                              label={type.label}
                              value={type.value}
                            >
                              {type.label}
                            </Select.Option>
                          ))}
                        </SelectField>
                      </FormItem>
                    </>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.ASSIGNEE}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <Form.Item
                      label={label}
                      name="assignee"
                      required={required}
                      rules={[{ required }]}
                    >
                      <AssigneeSearch
                        allowedUserEmails={allowedUserEmails}
                        disabled={disabled}
                        users={users}
                      />
                    </Form.Item>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.NAME}>
                  <LockedActivityName
                    activityTypes={activityTypes}
                    placeholder={intl.formatMessage(messages.autoFilled)}
                    {...lockedFieldsProps?.[ActivityFieldName.NAME]}
                  />
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.COMPANY}>
                  {({ label, required }: ConditionalFormFieldRenderProps) => (
                    <Tooltip
                      title={intl.formatMessage(
                        messages.autoFillingRelationshipFieldDisabledTooltip
                      )}
                    >
                      <LockedEntityType
                        entityType={EntityType.COMPANY}
                        label={label}
                        placeholder={intl.formatMessage(messages.select)}
                        required={required}
                        {...lockedFieldsProps?.[ActivityFieldName.COMPANY]}
                      />
                    </Tooltip>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.PERSON}>
                  {({ label, required }: ConditionalFormFieldRenderProps) => (
                    <Tooltip
                      title={intl.formatMessage(
                        messages.autoFillingRelationshipFieldDisabledTooltip
                      )}
                    >
                      <LockedEntityType
                        entityType={EntityType.PERSON}
                        label={label}
                        placeholder={intl.formatMessage(messages.select)}
                        required={required}
                        {...lockedFieldsProps?.[ActivityFieldName.PERSON]}
                      />
                    </Tooltip>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.DEAL}>
                  {({ label, required }: ConditionalFormFieldRenderProps) => (
                    <Tooltip
                      title={intl.formatMessage(
                        messages.autoFillingRelationshipFieldDisabledTooltip
                      )}
                    >
                      <LockedEntityType
                        entityType={EntityType.DEAL}
                        label={label}
                        placeholder={intl.formatMessage(messages.select)}
                        required={required}
                        {...lockedFieldsProps?.[ActivityFieldName.DEAL]}
                      />
                    </Tooltip>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.NOTE}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, curValues) =>
                        prevValues.note !== curValues.note ||
                        prevValues.visibility !== curValues.visibility
                      }
                    >
                      {({ getFieldValue }) => (
                        <Form.Item
                          label={label}
                          name={ActivityFieldName.NOTE}
                          required={required}
                          rules={[{ required }]}
                        >
                          <TextAreaWithMentions
                            allUsers={users}
                            disabled={disabled}
                            emptyTextForWithSkippedUsers={intl.formatMessage(
                              messages.emptyTextForWithSkippedUsers
                            )}
                            hideDisabledUsers
                            isMentionDisabled={handleIsMentionDisabled}
                            key={
                              getFieldValue(ActivityFieldName.VISIBILITY) +
                              stringHash(allowedUserEmails?.join() ?? "")
                            }
                            maxLength={Number.MAX_SAFE_INTEGER}
                            teams={teams}
                          />
                        </Form.Item>
                      )}
                    </Form.Item>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.VISIBILITY}>
                  {({ disabled, required }: ConditionalFormFieldRenderProps) => (
                    <Form.Item
                      label={intl.formatMessage(messages.visibility)}
                      required={required}
                      shouldUpdate={(prev, next) =>
                        prev.visibility !== next.visibility || prev.teamIds !== next.teamIds
                      }
                    >
                      {({ getFieldValue, setFieldsValue }) => (
                        <VisibilitySelectField<ActivityVisibility>
                          disabled={disabled}
                          noteGetter={getActivityVisibilityNote}
                          onChange={(visibility: ActivityVisibility) =>
                            setFieldsValue({ visibility })
                          }
                          onTeamIdsChange={(teamIds: Team["id"][]) => setFieldsValue({ teamIds })}
                          options={visibilityOptions}
                          teamIds={getFieldValue("teamIds")}
                          teams={teams}
                          teamsRenderer={(node) => (
                            <Form.Item name={"teamIds"} noStyle>
                              {node}
                            </Form.Item>
                          )}
                          value={getFieldValue(ActivityFieldName.VISIBILITY)}
                          visibilityRenderer={(node) => (
                            <Form.Item name={ActivityFieldName.VISIBILITY} noStyle>
                              {node}
                            </Form.Item>
                          )}
                        />
                      )}
                    </Form.Item>
                  )}
                </ConditionalFormField>
              </FormFields>
            )}
          </div>
          <div className={styles.footer}>
            <div className={styles.left}>
              <div className={styles.footerInfo}>
                <div className={styles.clockIcon}>
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) =>
                    prevValues.startAt !== curValues.startAt || prevValues.endAt !== curValues.endAt
                  }
                >
                  <div className={styles.group}>
                    <ActivityDate form={form} onChange={handleFormChangeRerender} />
                  </div>
                </Form.Item>
              </div>
              <ActivityCompleted form={form} />
            </div>
            <div>
              <Button
                disabled={hasRequiredUniqueCustomFields || hasRequiredRelationshipField}
                key="submit"
                onClick={form.submit}
                type="primary"
              >
                {intl.formatMessage(messages.confirmButton, {
                  count: activities.length,
                  isCompleted,
                })}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </EntityModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  currentUser: getCurrentUser(state)!,
  isManager: isCurrentUserManager(state),
  isOwner: isCurrentUserOwner(state),
  me: getMe(state)!,
  teams: getTeams(state),
  users: getUsersOfEntireOrganization(state),
});

const mapDispatchToProps = {
  onBulkCreate: bulkCreateActivities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkActivityCreateModal);
